import React from 'react'
import { TextInput } from '@/components/common/text-input'
import {
  AddIcon,
  SearchActiveIcon,
  SearchBtnIcon,
  SearchIcon
} from '@/components/agent-dashboard/assets';
import { useTranslation } from 'react-i18next';
import { Dropdown } from '@/components/ui/modal';
import { TextButton } from '@/components/common/text-button';
import { cn } from '@/lib/utils';
import { SearchFormData } from '@/manager/manager/agent-dashboard-permission-manager';
import { CreateAdminModal } from './create-admin-modal';
import { AddAdminIcon } from './assets';
import { ExpirationVerify } from '../agent-dashboard/expiration-verify';

interface SearchBarProps {
  searchFormData: SearchFormData,
  search: (page: number) => void,
  changeSearchForm: (key: keyof SearchFormData, value: SearchFormData[keyof SearchFormData]) => void,
  resetAndSearch: () => void,
}

/** 智能体权限管理-搜索栏 */
export const SearchBar: React.FC<SearchBarProps> = ({
  search,
  searchFormData,
  changeSearchForm,
  resetAndSearch
}) => {
  const { t } = useTranslation();

  const handleSearch = () => {
    search(1);
  }

  const handleReset = () => {
    resetAndSearch();
  }

  return (
    <div className='flex items-end justify-between'>
      <div className='flex flex-wrap gap-3'>
        <div>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>{t('agent_dashboard.permission.search.conditional_filtering')}</label>
          <Dropdown
            value={searchFormData?.searchType}
            placeholder={t('agent_dashboard.permission.search.please_select')}
            className='h-9'
            onValueChange={(value) => changeSearchForm('searchType', value)}
            width='11.3rem'
            options={[
              { value: 'user_name', label: t('agent_dashboard.permission.search.filter.name') },
              { value: 'email', label: t('agent_dashboard.permission.search.filter.email_address') },
              { value: 'user_id', label: t('agent_dashboard.permission.search.filter.user_id') },
            ]}
          />
        </div>
        <div className='flex items-end'>
          <TextInput
            value={searchFormData.inputText}
            placeholder={t('agent_dashboard.permission.search.search_placeholder')}
            className='w-[25rem] h-9 flex-1 font-normal'
            prefixIcon={<SearchIcon className='w-5 h-5' />}
            prefixActiveIcon={<SearchActiveIcon className='w-5 h-5 font-normal' />}
            onChange={(e) => changeSearchForm('inputText', e.target.value)}
          />
        </div>
        <div>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>
            {t('agent_dashboard.permission.search.role')}
          </label>
          <Dropdown
            value={searchFormData.roleType?.toString() || ''}
            placeholder={t('agent_dashboard.permission.search.please_select')}
            className='h-9'
            onValueChange={(value) => changeSearchForm('roleType', Number(value))}
            width='10rem'
            options={[
              { value: '3', label: t('agent_dashboard.permission.search.role_options.super_admin') },
              { value: '2', label: t('agent_dashboard.permission.search.role_options.admin') },
              { value: '1', label: t('agent_dashboard.permission.search.role_options.operator') },
            ]}
          />
        </div>
        <div>
          <label className='text-sm font-medium text-[rgba(52,64,84,1)] mb-1.5 inline-block'>
            {t('agent_dashboard.permission.search.status')}
          </label>
          <Dropdown
            value={searchFormData.roleStatus?.toString() || ''}
            placeholder={t('agent_dashboard.permission.search.please_select')}
            className='h-9'
            onValueChange={(value) => changeSearchForm('roleStatus', Number(value))}
            width='10rem'
            options={[
              { value: '1', label: t('agent_dashboard.permission.search.status_options.active') },
              { value: '0', label: t('agent_dashboard.permission.search.status_options.disabled') },
            ]}
          />
        </div>
        <div className='flex items-end space-x-3'>
          <TextButton
            label={t('agent_dashboard.user_management.search.reset')}
            className={cn(
              'max-w-max !px-5 h-9 bg-white',
              'font-semibold text-sm !text-[rgba(65,70,81,1)]',
              'border border-[rgba(213, 215, 218, 1)] hover:bg-gray-50',
            )}
            onClick={handleReset}
          />
          <TextButton
            label={t('agent_dashboard.user_management.search.search')}
            icon={<SearchBtnIcon className='w-5 h-5' />}
            className={cn(
              '!w-[6.4rem] h-9 ',
              'text-white font-semibold text-sm',
            )}
            onClick={handleSearch}
          />
        </div>
      </div>
      <CreateAdminModal
        trigger={
          <ExpirationVerify>
            <TextButton
              icon={<AddAdminIcon className="size-5" />}
              label={t('agent_dashboard.permission.add_admin')}
              className={cn(
                'max-w-max h-9',
                'text-white font-semibold text-sm flex-shrink-0',
              )}
            />
          </ExpirationVerify>
        }
      />
    </div>
  )
}
